import React, { useMemo, useState } from "react";
import {
  Timer,
  Inbox,
  Clock,
  Hourglass,
  TrendingDown,
  TrendingUp,
  Minus,
  BarChart3,
  LayoutGrid,
  CheckCircle2,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import type { Deal, Task, DealNote, Sale } from "../types";
import type { FilterOptions } from "./dashboardUtils";
import {
  calculateResponseTimeDashboardData,
  type ResponseTimeVariation,
} from "./responseTimeUtils";

interface ResponseTimeCardProps {
  deals: Deal[];
  tasks: Task[];
  dealNotes: DealNote[];
  salesTeam: Sale[];
  filters: FilterOptions;
}

type ViewMode = "resumo" | "ranking";

interface VariationBadgeProps {
  variation: ResponseTimeVariation;
  /** Se true, queda no valor é positiva (verde) e subida é negativa (vermelho) */
  lowerIsBetter?: boolean;
}

const VariationBadge: React.FC<VariationBadgeProps> = ({
  variation,
  lowerIsBetter = false,
}) => {
  if (!variation.hasPreviousData || variation.percentChange === null) {
    return (
      <span className="text-[11px] text-muted-foreground">
        {variation.periodLabel}
      </span>
    );
  }

  const pct = variation.percentChange;
  const absPct = Math.abs(pct).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  if (Math.abs(pct) < 0.05) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
        <Minus className="w-3 h-3" />
        Estável ({variation.periodLabel})
      </span>
    );
  }

  const isDown = pct < 0;
  const isGood = lowerIsBetter ? isDown : !isDown;

  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-medium ${
        isGood
          ? "text-emerald-600 dark:text-emerald-400"
          : "text-rose-600 dark:text-rose-400"
      }`}
    >
      {isDown ? (
        <TrendingDown className="w-3 h-3 shrink-0" />
      ) : (
        <TrendingUp className="w-3 h-3 shrink-0" />
      )}
      <span>
        {isDown ? `-${absPct}%` : `+${absPct}%`} {variation.periodLabel}
      </span>
    </span>
  );
};

export const ResponseTimeCard: React.FC<ResponseTimeCardProps> = ({
  deals,
  tasks,
  dealNotes,
  salesTeam,
  filters,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>("resumo");

  const data = useMemo(
    () =>
      calculateResponseTimeDashboardData(
        deals,
        tasks,
        dealNotes,
        salesTeam,
        filters,
      ),
    [deals, tasks, dealNotes, salesTeam, filters],
  );

  const { current, previous, variations, ranking } = data;

  const waitingPercent =
    current.leadsReceived > 0
      ? ((current.leadsWaiting / current.leadsReceived) * 100).toLocaleString(
          "pt-BR",
          {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          },
        )
      : "0,0";

  const respondedPercent =
    current.leadsReceived > 0
      ? ((current.leadsResponded / current.leadsReceived) * 100).toLocaleString(
          "pt-BR",
          {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          },
        )
      : "0,0";

  const maxRankMinutes = useMemo(() => {
    const valid = ranking
      .map((r) => r.avgMinutes)
      .filter((v): v is number => v !== null && v > 0);
    return valid.length > 0 ? Math.max(...valid) : 1;
  }, [ranking]);

  return (
    <div className="bg-card border border-border/80 rounded-xl p-4 shadow-xs">
      {/* Cabeçalho com Toggle Resumo / Ranking */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-border/60">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-cyan-500/10 text-cyan-500 flex items-center justify-center">
            <Timer className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Response Time
            </h3>
            <p className="text-[11px] text-muted-foreground">
              Tempo até a primeira atividade executada pelo proprietário após a
              criação do negócio
            </p>
          </div>
        </div>

        {/* Botão de alternância (toggle) entre "Resumo" e "Ranking" */}
        <div className="inline-flex items-center rounded-lg border border-border/80 bg-muted/40 p-0.5 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => setViewMode("resumo")}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer ${
              viewMode === "resumo"
                ? "bg-background text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5" />
            Resumo
          </button>
          <button
            type="button"
            onClick={() => setViewMode("ranking")}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer ${
              viewMode === "ranking"
                ? "bg-background text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <BarChart3 className="w-3.5 h-3.5" />
            Ranking
          </button>
        </div>
      </div>

      {/* Visualização 1: Visão Geral (Resumo) */}
      {viewMode === "resumo" ? (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Card 1: Leads recebidos */}
          <div className="rounded-xl border border-border/60 bg-muted/15 p-3.5 flex flex-col justify-between gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Leads Recebidos
              </span>
              <div className="w-7 h-7 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center">
                <Inbox className="w-3.5 h-3.5" />
              </div>
            </div>
            <div>
              <span className="text-2xl font-bold text-foreground tracking-tight">
                {current.leadsReceived}
              </span>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {current.leadsResponded} atendidos ({respondedPercent}%)
              </p>
            </div>
            <div className="pt-2 border-t border-border/50 flex items-center justify-between">
              <VariationBadge
                variation={variations.leadsReceived}
                lowerIsBetter={false}
              />
              {previous && (
                <span className="text-[10px] text-muted-foreground">
                  Ant.: {previous.leadsReceived}
                </span>
              )}
            </div>
          </div>

          {/* Card 2: Média em minutos */}
          <div className="rounded-xl border border-border/60 bg-muted/15 p-3.5 flex flex-col justify-between gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Média em Minutos
              </span>
              <div className="w-7 h-7 rounded-lg bg-cyan-500/10 text-cyan-500 flex items-center justify-center">
                <Timer className="w-3.5 h-3.5" />
              </div>
            </div>
            <div>
              <span className="text-2xl font-bold text-foreground tracking-tight">
                {current.avgMinutesFormatted}
              </span>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Baseado em {current.leadsResponded}{" "}
                {current.leadsResponded === 1
                  ? "lead atendido"
                  : "leads atendidos"}
              </p>
            </div>
            <div className="pt-2 border-t border-border/50 flex items-center justify-between">
              <VariationBadge
                variation={variations.avgMinutes}
                lowerIsBetter={true}
              />
              {previous && previous.avgMinutes !== null && (
                <span className="text-[10px] text-muted-foreground">
                  Ant.: {previous.avgMinutesFormatted}
                </span>
              )}
            </div>
          </div>

          {/* Card 3: Tempo médio (formato legível) */}
          <div className="rounded-xl border border-border/60 bg-muted/15 p-3.5 flex flex-col justify-between gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Tempo Médio
              </span>
              <div className="w-7 h-7 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                <Clock className="w-3.5 h-3.5" />
              </div>
            </div>
            <div>
              <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 tracking-tight">
                {current.avgReadable}
              </span>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Tempo médio de 1º contato
              </p>
            </div>
            <div className="pt-2 border-t border-border/50 flex items-center justify-between">
              <VariationBadge
                variation={variations.avgMinutes}
                lowerIsBetter={true}
              />
              {previous && previous.avgMinutes !== null && (
                <span className="text-[10px] text-muted-foreground">
                  Ant.: {previous.avgReadable}
                </span>
              )}
            </div>
          </div>

          {/* Card 4: Aguardando 1º contato */}
          <div className="rounded-xl border border-border/60 bg-muted/15 p-3.5 flex flex-col justify-between gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Aguardando 1º Contato
              </span>
              <div className="w-7 h-7 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center">
                <Hourglass className="w-3.5 h-3.5" />
              </div>
            </div>
            <div>
              <span className="text-2xl font-bold text-amber-600 dark:text-amber-400 tracking-tight">
                {current.leadsWaiting}
              </span>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {waitingPercent}% dos leads sem atividade (fora da média)
              </p>
            </div>
            <div className="pt-2 border-t border-border/50 flex items-center justify-between">
              <VariationBadge
                variation={variations.leadsWaiting}
                lowerIsBetter={true}
              />
              {previous && (
                <span className="text-[10px] text-muted-foreground">
                  Ant.: {previous.leadsWaiting}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Visualização 2: Ranking do Time (Barras Horizontais) */
        <div className="mt-4">
          {ranking.length === 0 ? (
            <div className="text-center py-8 text-xs text-muted-foreground">
              Nenhum negócio recebido no período selecionado.
            </div>
          ) : (
            <div className="space-y-3">
              {ranking.map((member, index) => {
                const hasResponseTime = member.avgMinutes !== null;
                const widthPercent =
                  hasResponseTime && member.avgMinutes !== null
                    ? Math.max(
                        6,
                        Math.min(
                          (member.avgMinutes / maxRankMinutes) * 100,
                          100,
                        ),
                      )
                    : 0;

                // Cor da barra conforme velocidade de resposta
                const barColorClass = !hasResponseTime
                  ? "bg-muted"
                  : (member.avgMinutes ?? 0) <= 60
                    ? "bg-emerald-500"
                    : (member.avgMinutes ?? 0) <= 240
                      ? "bg-cyan-500"
                      : (member.avgMinutes ?? 0) <= 1440
                        ? "bg-amber-500"
                        : "bg-rose-500";

                const rankMedal =
                  hasResponseTime && index === 0
                    ? "🥇"
                    : hasResponseTime && index === 1
                      ? "🥈"
                      : hasResponseTime && index === 2
                        ? "🥉"
                        : `${index + 1}º`;

                return (
                  <div
                    key={member.salesId}
                    className="p-3 rounded-lg border border-border/50 bg-muted/15 hover:border-border transition-all space-y-2"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      {/* Identificação do Membro */}
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="text-xs font-bold text-muted-foreground w-6 text-center shrink-0">
                          {rankMedal}
                        </span>
                        <Avatar className="w-7 h-7 text-xs font-semibold border shrink-0">
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {member.initials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <span className="text-xs font-semibold text-foreground truncate block">
                            {member.name}
                          </span>
                          <span className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
                            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                              <CheckCircle2 className="w-3 h-3" />
                              {member.leadsResponded}{" "}
                              {member.leadsResponded === 1
                                ? "lead atendido"
                                : "leads atendidos"}
                            </span>
                            <span>•</span>
                            <span>
                              {member.leadsReceived}{" "}
                              {member.leadsReceived === 1
                                ? "recebido"
                                : "recebidos"}
                            </span>
                            {member.leadsWaiting > 0 && (
                              <>
                                <span>•</span>
                                <span className="text-amber-600 dark:text-amber-400">
                                  {member.leadsWaiting} aguardando 1º contato
                                </span>
                              </>
                            )}
                          </span>
                        </div>
                      </div>

                      {/* Tempo Médio do Membro */}
                      <div className="flex items-baseline gap-2 sm:text-right pl-8 sm:pl-0 shrink-0">
                        {hasResponseTime ? (
                          <>
                            <span className="text-sm font-bold text-foreground">
                              {member.avgReadable}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              ({member.avgMinutesFormatted})
                            </span>
                          </>
                        ) : (
                          <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                            Sem 1º contato registrado
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Barra Horizontal */}
                    <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${barColorClass}`}
                        style={{ width: `${widthPercent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
